import React from 'react';
import { Form, Responsive, Localization } from 'connex-cds';
import { find, get } from 'lodash';
import { useDoOnce } from '../../../../../../../util/useDoOnce';
import { useTicketContext } from '../../../TicketContext';
import { ListDesktop } from './desktop/ListDesktop';
import { ListMobile } from './mobile/ListMobile';
import {
  useCompanySetup,
  useProductSetup,
  useFormsAndWaiversSetup,
  useVehicleSetup,
  useVehicleTypeSetup,
} from '../../../MasterDataProvider';
import { activeFormsAndWaiversArray } from '../../../getActiveForms';

export const CustomListComponent = props => {
  const {
    deleteItem,
    resetForm,
    parentFormContext: { errors, values: parentValues, setFieldValue: setParentFieldValue },
    mode,
  } = Form.useFormContext();
  const { ticket, paymentMethod, isStandAloneTicket } = useTicketContext();
  const companySetup = useCompanySetup();
  const productSetup = useProductSetup();

  const formsAndWaiversSetup = useFormsAndWaiversSetup();
  const vehicleSetup = useVehicleSetup();
  const vehicleTypeSetup = useVehicleTypeSetup();
  const { translateObjects } = Localization.useTranslateObjects();
  const { userLocale } = Localization.useLocalizationContext();

  const setupItems = React.useMemo(
    () => productSetup?.data?.filter?.(product => product.status === 'ACTIVE'),
    [productSetup?.data]
  );

  const isSlumpRackWaterAtPlant = React.useMemo(
    () => companySetup?.data?.isSlumpRackWaterAtPlant ?? false,
    [companySetup?.data?.isSlumpRackWaterAtPlant]
  );

  const stepsValidation = React.useMemo(
    () =>
      isSlumpRackWaterAtPlant
        ? ['LOADING_COMPLETE', 'TO_JOB', 'ARRIVE_JOB', 'UNLOADING', 'END_UNLOADING', 'PUMPING_COMPLETE']
        : ['END_UNLOADING', 'PUMPING_COMPLETE'],
    [isSlumpRackWaterAtPlant]
  );

  const handleDeleteClick = React.useCallback(
    row => {
      const isWaterProduct = Boolean(row?.waterProduct);
      const productItem = find(setupItems, { crn: row?.item?.productRef });
      const isDriverSellable = get(productItem, 'isDriverSellable', false);

      if (isWaterProduct || isDriverSellable) {
        deleteItem(row);
        resetForm({ reinitialize: true });
      }
    },
    [deleteItem, resetForm, setupItems]
  );

  const handleNoWaterAdded = React.useCallback(
    idx => {
      const events = [...(parentValues?.waterAddedEvents ?? [])];
      const prev = events[idx];
      if (!prev) {
        return;
      }
      events[idx] = {
        ...prev,
        driverDidNotAdd: true,
        reason: undefined,
        concreteOnTruck: undefined,
        time: undefined,
        waterLocation: undefined,
        quantity: companySetup?.data?.isWaterMandatory
          ? { ...prev.quantity, value: '0' }
          : undefined,
      };
      setParentFieldValue('waterAddedEvents', events);
      resetForm({ reinitialize: true });
    },
    [setParentFieldValue, resetForm, companySetup?.data?.isWaterMandatory, parentValues?.waterAddedEvents]
  );

  const activeFormsAndWaivers = React.useMemo(
    () =>
      activeFormsAndWaiversArray({
        ticket,
        formTypeCondition: 'WATER',
        formsAndWaiversSetup,
        vehicleSetup,
        vehicleTypeSetup,
        companySetup,
        translateObjects,
        userLocale,
      }),
    [ticket, formsAndWaiversSetup, vehicleSetup, vehicleTypeSetup, companySetup, translateObjects, userLocale]
  );

  const isWaterReleaseMandatory = React.useMemo(
    () =>
      !!companySetup?.data?.isWaterMandatory &&
      !!companySetup?.data?.isWaterReleaseFormEnabled &&
      activeFormsAndWaivers?.some(item => item?.mandatoryWorkflow === 'MANDATORY'),
    [activeFormsAndWaivers, companySetup]
  );

  const waterReleaseSigned = React.useMemo(() => {
    return parentValues?.waterReleaseDeclined === false && parentValues?.waterReleaseSignature?.image;
  }, [parentValues]);

  const handleRowClick = React.useCallback(
    row => {
      const isWaterProduct = Boolean(row?.waterProduct);
      const productItem = find(setupItems, { crn: row?.item?.productRef });
      const isDriverSellable = get(productItem, 'isDriverSellable', false);
      const suggestedToDriver = get(row, 'suggestedToDriver', false);
      const ticketEventsExist = ticket?.ticketEvents && stepsValidation.some(event => ticket?.ticketEvents[event]);

      if (
        (ticketEventsExist ||
          !(suggestedToDriver && !(isWaterProduct && (!isWaterReleaseMandatory || waterReleaseSigned !== false))) ||
          isStandAloneTicket ||
          props?.['data-testid'] === 'lineItems') &&
        (isDriverSellable || (isWaterProduct && (!isWaterReleaseMandatory || waterReleaseSigned !== false)))
      ) {
        props.onRowClick?.(row);
      }
    },
    [props, ticket?.ticketEvents, stepsValidation, setupItems, waterReleaseSigned, isWaterReleaseMandatory]
  );

  React.useEffect(() => {
    if (!waterReleaseSigned || !isWaterReleaseMandatory) {
      return;
    }

    if (!errors?.[props.paths.errors] || mode === 'edit' || !props?.data?.[0]) {
      return;
    }

    const firstRow = props.data[0];
    const timeoutId = setTimeout(() => {
      handleRowClick(firstRow);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [waterReleaseSigned, isWaterReleaseMandatory, errors, props.paths.errors]);

  useDoOnce(() => {
    if (props.autoEdit && errors[props.paths.errors] && mode !== 'edit' && props?.data?.[0]) {
      // Have to wait a tick in order for things to be ready.
      setTimeout(() => {
        props.onRowClick?.(props.data[0]);
      });
    }
  });

  const disabled = React.useCallback(
    row => {
      const ticketEventsExist = !Object.keys(ticket?.ticketEvents ?? {}).some(event => stepsValidation.includes(event));
      const productItem = find(setupItems, { crn: row?.item?.productRef });
      const isWaterProduct = Boolean(row?.waterProduct);
      const suggestedToDriver = get(row, 'suggestedToDriver', false);
      const isDriverSellable = get(productItem, 'isDriverSellable', false);
      const isDriverAllowedToEditWater = companySetup?.data?.isDriverAllowedToEditWater !== false;
      // If it's a water product and driver is allowed to edit water, don't disable it
      if (isWaterProduct && isDriverAllowedToEditWater === true) {
        return false;
      }

      // If it's a water product and driver is not allowed to edit water, disable it
      if (isWaterProduct && isDriverAllowedToEditWater === false) {
        return true;
      }

      if (!isDriverSellable) {
        return (
          !isWaterProduct ||
          (ticketEventsExist && suggestedToDriver && !isStandAloneTicket && !(props?.['data-testid'] === 'lineItems'))
        );
      }
      return ticketEventsExist && suggestedToDriver && !isStandAloneTicket && !(props?.['data-testid'] === 'lineItems');
    },
    [ticket?.ticketEvents, stepsValidation, setupItems, props, companySetup?.data?.isDriverAllowedToEditWater]
  );

  const listProps = React.useMemo(() => {
    return {
      ...props,
      handleDeleteClick,
      handleNoWaterAdded,
      handleRowClick,
      disabled,
      errors,
      paymentMethod,
      isWaterReleaseMandatory,
    };
  }, [disabled, errors, handleDeleteClick, handleNoWaterAdded, handleRowClick, isWaterReleaseMandatory, props]);

  return (
    <Responsive>
      <ListMobile {...listProps} />
      <ListDesktop {...listProps} />
    </Responsive>
  );
};
